"use client";
import { useSession } from "next-auth/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import {
  TRACKING_ID_PARAM,
  useTrackingId,
} from "~/components/analytics/useTrackingId";
import {
  EmailInput,
  OAuthButtons,
  SuccessAlert,
} from "~/components/auth";
import ErrorMessage from "~/components/ErrorMessage";
import { LoadingPage } from "~/components/loading";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { authService } from "~/connectors/auth.connector";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

type LoginFormProps = {
  title?: string;
  redirectUrl?: string;
};

function LoginFormContent({
  title = "Bun venit!",
  redirectUrl: callbackUrl,
}: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const trackingId = useTrackingId();

  // Redirect if already authenticated
  useEffect(() => {
    if (status === "loading" || !router) return;
    if (session) {
      // Skip automatic redirect in order flow - let the page handle it
      if (pathname === "/comanda/autentificare") return;

      const redirectUrl =
        callbackUrl || searchParams.get("callbackUrl") || "/dashboard";
      router.push(redirectUrl);
      return;
    }
  }, [session, status, router, searchParams, callbackUrl, pathname]);

  // Check for auth errors from URL
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam) {
      let errorMessage = "Eroare de autentificare.";
      switch (errorParam) {
        case "Configuration":
          errorMessage =
            "Eroare de configurare. Te rugăm să contactezi suportul.";
          break;
        case "AccessDenied":
          errorMessage = "Acces respins. Te rugăm să încerci din nou.";
          break;
        case "Verification":
          errorMessage = "Link-ul de verificare este invalid sau a expirat.";
          break;
        case "OAuthAccountNotLinked":
          errorMessage =
            "Contul OAuth nu este conectat. Te rugăm să folosești aceeași metodă de autentificare ca și prima dată.";
          break;
        case "Default":
          errorMessage = "A apărut o eroare neașteptată.";
          break;
        default:
          errorMessage = `Eroare: ${errorParam}`;
      }
      setError(errorMessage);
    }

    // Check if redirected back after sending magic link
    const callbackUrl = searchParams.get("callbackUrl");
    if (callbackUrl && email && !magicLinkSent) {
      setMagicLinkSent(true);
      setShowEmailInput(true);
    }
  }, [searchParams, email, magicLinkSent]);

  const handleMagicLink = async () => {
    if (!showEmailInput) {
      trackEvent(AnalyticsEvents.USER_LOGIN_EMAIL_CLICKED);
      setShowEmailInput(true);
      return;
    }

    if (!email) {
      setError(
        "Te rugăm să introduci email-ul pentru a putea primi link-ul de acces.",
      );
      return;
    }

    setError("");
    setLoading(true);

    // if error query param exists, clear it without navigating, dont use window
    const errorParam = searchParams.get("error");
    if (errorParam) {
      const redirectUrl =
        callbackUrl || searchParams.get("callbackUrl") || "/dashboard";
      router.replace(`/login?callbackUrl=${encodeURIComponent(redirectUrl)}`);
    }

    try {
      const redirectUrl =
        callbackUrl || searchParams.get("callbackUrl") || "/dashboard";
      const callbackUrlWithTracking = trackingId
        ? `${redirectUrl}${redirectUrl.includes("?") ? "&" : "?"}${TRACKING_ID_PARAM}=${trackingId}`
        : redirectUrl;
      const result = await authService.loginWithEmail(
        email,
        callbackUrlWithTracking,
      );

      if (result?.error) {
        let errorMessage =
          "Eroare la trimiterea link-ului de acces. Te rugăm să încerci din nou.";
        console.log(result.error);
        if (result.error.includes("mailgun") || result.error.includes("api")) {
          errorMessage =
            "Eroare de configurare email. Te rugăm să contactezi suportul.";
        }
        setError(errorMessage);
      } else {
        setMagicLinkSent(true);
        setShowEmailInput(false); // Hide input after sending
      }
    } catch {
      setError("Eroare de rețea. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    trackEvent(AnalyticsEvents.USER_LOGIN_GOOGLE_CLICKED);
    setError("");
    const redirectUrl =
      callbackUrl || searchParams.get("callbackUrl") || "/dashboard";
    const callbackUrlWithTracking = trackingId
      ? `${redirectUrl}${redirectUrl.includes("?") ? "&" : "?"}tid=${trackingId}`
      : redirectUrl;
    try {
      await authService.loginWithGoogle(callbackUrlWithTracking);
    } catch {
      setError("Eroare la autentificare cu Google.");
    }
  };

  const handleResend = () => {
    setMagicLinkSent(false);
    setShowEmailInput(true);
    setError("");
  };

  const handleBackToAuthOptions = () => {
    setShowEmailInput(false);
    setError("");
  };

  if (status === "loading") {
    return <LoadingPage />;
  }

  return (
    <>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            mb: 2,
            fontFamily: "var(--font-body)",
            fontWeight: 700,
          }}
        >
          {magicLinkSent ? "Vezi pe mail" : title}
        </Typography>
        {!magicLinkSent && (
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {showEmailInput
              ? "Introdu email-ul pentru a primi un link de acces"
              : "Alege cum vrei să intri"}
          </Typography>
        )}
      </Box>
      <ErrorMessage error={error} />
      {magicLinkSent && <SuccessAlert email={email} onResend={handleResend} />}
      {!showEmailInput && !magicLinkSent && (
        <OAuthButtons onGoogleSignIn={handleGoogleSignIn} />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <EmailInput
          email={email}
          onChange={setEmail}
          show={showEmailInput && !magicLinkSent}
        />
        {!magicLinkSent && (
          <>
            <Button
              onClick={handleMagicLink}
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{
                py: 1.5,
              }}
            >
              {loading
                ? "Se trimite..."
                : showEmailInput
                  ? "Trimite link de acces"
                  : "Continuă cu email"}
            </Button>
            {showEmailInput && (
              <Button
                onClick={handleBackToAuthOptions}
                fullWidth
                variant="outlined"
                color="inherit"
              >
                ← Înapoi la opțiunile de autentificare
              </Button>
            )}
          </>
        )}
      </Box>
    </>
  );
}

export default function LoginForm(props: LoginFormProps) {
  return (
    <Suspense fallback={<LoadingPage />}>
      <LoginFormContent {...props} />
    </Suspense>
  );
}
